
import React, { useEffect, useMemo, useContext } from 'react';
import { EmailContextType } from '../types';
import { EmailContext } from './EmailContext';
import { useEmailState } from './useEmailState';
import { useEmailComposeMethods } from './useEmailComposeMethods';
import { useEmailContextOperations } from './useEmailContextOperations';
import { useEmailBootstrap } from '../hooks/useEmailBootstrap';
import { useAuth } from '@/context/AuthContext';
import { userEmailConfigApi } from '@/services/api/userEmailConfig';

export const useEmail = () => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error('useEmail must be used within an EmailProvider');
  }
  return context;
};

export const EmailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('📧 EmailProvider: Rendering...');
  const { user } = useAuth();
  
  const {
    emails,
    currentEmail,
    loading,
    error,
    isDbReady,
    currentFolder,
    searchQuery,
    composeOpen,
    composeDraft,
    statistics,
    folders,
    userEmailConfig,
    userDetails,
    hasMoreEmails,
    totalEmailsInFolder,
    totalPages,
    currentPage,
    pageSize,
    setCurrentFolder,
    setSearchQuery,
    setComposeOpen,
    setComposeDraft,
    stableSetEmails,
    stableSetCurrentEmail,
    stableSetLoading,
    stableSetError,
    stableSetStatistics,
    stableSetUserEmailConfig,
    stableSetUserDetails,
    stableSetHasMoreEmails,
    stableSetTotalEmailsInFolder,
    stableSetTotalPages,
  } = useEmailState();

  // Bootstrap user email configuration
  useEmailBootstrap({
    user,
    setUserDetails: stableSetUserDetails,
    setUserEmailConfig: stableSetUserEmailConfig,
    setLoading: stableSetLoading,
    setError: stableSetError,
  });

  const { updateComposeDraft, resetComposeDraft } = useEmailComposeMethods(setComposeDraft);

  const { operations, loadFolderEmails, setSelectedEmail } = useEmailContextOperations({
    emails,
    stableSetEmails,
    stableSetCurrentEmail,
    stableSetLoading,
    stableSetError,
    stableSetStatistics,
    stableSetHasMoreEmails,
    stableSetTotalEmailsInFolder,
    stableSetTotalPages,
    currentFolder,
  });

  // Initialize database and load statistics only once
  useEffect(() => {
    let isMounted = true;
    
    const initialize = async () => {
      console.log('📧 EmailProvider: Initializing...');
      try {
        await operations.initializeDatabase();
        if (isMounted) {
          console.log('📧 EmailProvider: Loading statistics in background');
          operations.loadStatistics();
        }
      } catch (error) {
        console.error('📧 EmailProvider: Initialization failed:', error);
        if (isMounted) {
          stableSetError(true);
        }
      }
    };

    initialize();
    
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<EmailContextType>(() => ({
    // State
    emails,
    folders,
    currentFolder,
    currentEmail,
    selectedEmail: currentEmail,
    loading,
    error,
    searchQuery,
    composeOpen,
    composeDraft,
    statistics,
    isDbReady,
    userEmailConfig,
    userDetails,
    hasMoreEmails,
    totalEmailsInFolder,
    totalPages,
    currentPage,
    pageSize,
    
    // State setters
    setCurrentFolder,
    setSearchQuery,
    setComposeOpen,
    setComposeDraft,
    
    // Operations
    loadFolderEmails,
    fetchEmails: operations.fetchEmails,
    getEmail: operations.getEmail,
    getEmailById: operations.getEmail, // Alias for compatibility
    markAsRead: async (id: string) => {
      await operations.markAsRead(id);
    },
    markAsUnread: async (id: string) => {
      await operations.markAsUnread(id);
    },
    moveToTrash: async (id: string) => {
      await operations.moveToTrash(id);
    },
    moveToFolder: operations.moveToFolder,
    deleteEmail: operations.deleteEmail,
    sendEmail: operations.sendEmail,
    saveDraft: operations.saveDraft,
    loadDraft: operations.loadDraft,
    sendDraft: operations.sendDraft,
    listDrafts: operations.listDrafts,
    syncWithServer: operations.syncWithServer,
    permanentlyDeleteEmail: async (id: string) => {
      await operations.permanentlyDeleteEmail(id);
    },
    refreshEmails: operations.refreshEmails,
    
    // User config operations
    loadUserConfig: async () => {
      if (!user?.id) return;
      const config = await userEmailConfigApi.getUserEmailConfig(user.id);
      stableSetUserEmailConfig(config);
    },
    
    // Compose operations
    updateComposeDraft,
    resetComposeDraft,
    
    // Additional operations
    setSelectedEmail,
  }), [
    emails,
    folders,
    currentFolder,
    currentEmail,
    loading,
    error,
    searchQuery,
    composeOpen,
    composeDraft,
    statistics,
    isDbReady,
    userEmailConfig,
    userDetails,
    user?.id,
    loadFolderEmails,
    operations,
    updateComposeDraft,
    resetComposeDraft,
    setSelectedEmail,
    stableSetUserEmailConfig,
  ]);

  console.log('📧 EmailProvider: Context value created', {
    emailsCount: emails.length,
    currentEmailId: currentEmail?.id,
    currentFolder,
    loading,
    error,
    unreadCount: statistics.unread
  });

  return (
    <EmailContext.Provider value={contextValue}>
      {children}
    </EmailContext.Provider>
  );
};
